'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { visitasService } from '@/lib/api/visitasService';
import { VisitaEntry } from '@/types/visita';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Modal } from '@/components/ui/Modal';
import { Icon } from '@/components/ui/icons';

// Función para convertir hora a zona horaria CDMX (UTC-5)
const convertirHoraCDMX = (fechaStr: string | undefined): string => {
  if (!fechaStr) return '—';
  
  try {
    // Parsear la fecha (puede venir en formato ISO o "YYYY-MM-DD HH:MM:SS")
    const fecha = new Date(fechaStr);
    
    // Restar 5 horas para convertir de UTC a CDMX (UTC-5)
    const fechaCDMX = new Date(fecha.getTime() - 5 * 60 * 60 * 1000);
    
    // Formatear como "YYYY-MM-DD HH:MM:SS"
    const year = fechaCDMX.getUTCFullYear();
    const month = String(fechaCDMX.getUTCMonth() + 1).padStart(2, '0');
    const day = String(fechaCDMX.getUTCDate()).padStart(2, '0');
    const hours = String(fechaCDMX.getUTCHours()).padStart(2, '0');
    const minutes = String(fechaCDMX.getUTCMinutes()).padStart(2, '0');
    const seconds = String(fechaCDMX.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('Error convirtiendo fecha:', error);
    return fechaStr;
  }
};

// Función para obtener el primer día del mes actual
const getPrimerDiaMes = (): string => {
  const hoy = new Date();
  const año = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  return `${año}-${mes}-01`;
};

// Función para obtener la fecha actual
const getFechaActual = (): string => {
  const hoy = new Date();
  const año = hoy.getFullYear();
  const mes = String(hoy.getMonth() + 1).padStart(2, '0');
  const dia = String(hoy.getDate()).padStart(2, '0');
  return `${año}-${mes}-${dia}`;
};

// Función para validar y ajustar fechas
const validarFecha = (fecha: string, esFin: boolean = false): string => {
  const fechaMinima = '2019-01-02';
  
  if (!fecha) {
    return esFin ? getFechaActual() : getPrimerDiaMes();
  }
  
  if (fecha < fechaMinima) {
    return fechaMinima;
  }
  
  return fecha;
};

// Función para formatear fecha para el backend (agregar hora)
const formatearFechaParaBackend = (fecha: string, esFin: boolean = false): string => {
  if (!fecha) return '';
  
  // Si ya tiene hora, retornar tal cual
  if (fecha.includes(' ')) return fecha;
  
  // Agregar hora según si es inicio o fin
  if (esFin) {
    return `${fecha} 23:59:59`;
  } else {
    return `${fecha} 00:00:00`;
  }
};

const VisitasManagement: React.FC = () => {
  const [visitas, setVisitas] = useState<VisitaEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fechaInicio, setFechaInicio] = useState(getPrimerDiaMes());
  const [fechaFin, setFechaFin] = useState(getFechaActual());
  const [editingVisita, setEditingVisita] = useState<VisitaEntry | null>(null);
  const [editCoords, setEditCoords] = useState('');
  const [editVisita, setEditVisita] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [visitaToDelete, setVisitaToDelete] = useState<VisitaEntry | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Validar y ajustar fechas
      const fechaInicioValidada = validarFecha(fechaInicio, false);
      const fechaFinValidada = validarFecha(fechaFin, true);
      
      // Formatear fechas para el backend
      const fechaInicioFormateada = formatearFechaParaBackend(fechaInicioValidada, false);
      const fechaFinFormateada = formatearFechaParaBackend(fechaFinValidada, true);
      
      const res = await visitasService.getAll(
        fechaInicioFormateada || undefined,
        fechaFinFormateada || undefined
      );
      
      if (res.data) {
        setVisitas(res.data);
      } else {
        setError(res.error || 'Error al cargar visitas');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar las visitas');
      console.error('Error fetching visitas:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Cargar visitas al montar el componente con fechas por defecto
    load();
  }, []);

  const rows = useMemo(() => {
    // Convertir todas las fechas a hora CDMX antes de mostrar
    return visitas.map(v => ({
      ...v,
      fecha: convertirHoraCDMX(v.fecha),
    }));
  }, [visitas]);

  const downloadCsv = () => {
    const header = ["FECHA", "USUARIO", "COORDENADAS", "MOTIVO DE VISITA"];
    const lines = rows.map((r) =>
      [
        r.fecha || "",
        r.usuario || "",
        r.coords || "",
        r.visita || "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ReportedeVisitas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleConsultar = () => {
    load();
  };

  const handleEdit = (visita: VisitaEntry) => {
    setEditingVisita(visita);
    setEditCoords(visita.coords || '');
    setEditVisita(visita.visita || '');
  };

  const handleSaveEdit = async () => {
    if (!editingVisita) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await visitasService.update(editingVisita.id, {
        coords: editCoords.trim() || undefined,
        visita: editVisita.trim() || undefined,
      });
      if (res.error) {
        setError(res.error);
      } else {
        setEditingVisita(null);
        load();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al actualizar la visita');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (visita: VisitaEntry) => {
    setVisitaToDelete(visita);
  };

  const handleConfirmDelete = async () => {
    if (!visitaToDelete) return;
    const id = visitaToDelete.id;
    setIsDeleting(id);
    setError(null);
    try {
      const res = await visitasService.delete(id);
      if (res.error) {
        setError(res.error);
      } else {
        setVisitaToDelete(null);
        load();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar la visita');
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Visitas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Consulta y reporte de visitas de campo registradas mediante geolocalización
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleConsultar} disabled={isLoading}>
            {isLoading ? "Consultando…" : "Consultar"}
          </Button>
          <Button onClick={downloadCsv} disabled={rows.length === 0 || isLoading}>
            Descargar CSV
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onRetry={handleConsultar} />}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              min="2019-01-02"
            />
            <p className="text-xs text-gray-500 mt-1">Fecha mínima: 2019-01-02</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              min="2019-01-02"
            />
            <p className="text-xs text-gray-500 mt-1">Fecha mínima: 2019-01-02</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div>
            Mostrando <span className="font-medium text-gray-700">{rows.length}</span> visita(s) 
            {fechaInicio && fechaFin && ` del ${fechaInicio} al ${fechaFin}`}
          </div>
          <div className="text-gray-400">
            Horas convertidas a zona horaria CDMX (UTC-5)
          </div>
        </div>
      </div>

      {/* Tabla con encabezado fijo y scroll */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 font-semibold border-b border-gray-200">FECHA</th>
                <th className="text-left p-3 font-semibold border-b border-gray-200">USUARIO</th>
                <th className="text-left p-3 font-semibold border-b border-gray-200">COORDENADAS</th>
                <th className="text-left p-3 font-semibold border-b border-gray-200">MOTIVO DE VISITA</th>
                <th className="text-center p-3 font-semibold border-b border-gray-200 w-24">ACCIONES</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.length === 0 ? (
                <tr>
                  <td className="p-6 text-gray-500 text-center" colSpan={5}>
                    {isLoading ? 'Cargando visitas...' : 'Sin registros en el rango de fechas seleccionado'}
                  </td>
                </tr>
              ) : (
                rows.map((v) => (
                  <tr key={v.id} className="hover:bg-blue-50/40 transition-colors">
                    <td className="p-3 font-mono text-xs">{v.fecha || '—'}</td>
                    <td className="p-3">{v.usuario || '—'}</td>
                    <td className="p-3 font-mono text-xs">{v.coords || '—'}</td>
                    <td className="p-3">{v.visita || '—'}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(v)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="Editar visita"
                        >
                          <Icon name="edit" size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(v)}
                          disabled={isDeleting === v.id}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Eliminar visita"
                        >
                          {isDeleting === v.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600"></div>
                          ) : (
                            <Icon name="trash" size={18} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {rows.length > 0 && (
        <div className="text-right text-xs text-gray-400 px-2">
          Total: {rows.length} visita(s) | Horas en zona horaria CDMX (UTC-5)
        </div>
      )}

      {/* Modal de edición */}
      <Modal
        isOpen={editingVisita !== null}
        onClose={() => setEditingVisita(null)}
        title="Editar Visita"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setEditingVisita(null)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coordenadas (latitud, longitud)
            </label>
            <input
              type="text"
              value={editCoords}
              onChange={(e) => setEditCoords(e.target.value)}
              placeholder="19.4412544, -99.2018432"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo de la visita
            </label>
            <textarea
              rows={3}
              value={editVisita}
              onChange={(e) => setEditVisita(e.target.value)}
              placeholder="Describe el motivo de la visita..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={visitaToDelete !== null}
        onClose={() => setVisitaToDelete(null)}
        confirmOnExit={false}
        title="Confirmar Eliminación"
        footer={
          <div className="flex justify-end gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setVisitaToDelete(null)} 
              disabled={isDeleting !== null}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              disabled={isDeleting !== null}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="trash" size={24} color="#dc2626" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                ¿Estás seguro de que deseas eliminar esta visita?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Esta acción no se puede deshacer. Se eliminará permanentemente el registro de la visita.
              </p>
              {visitaToDelete && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Fecha:</span>{' '}
                    <span className="text-gray-900">{visitaToDelete.fecha || '—'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Usuario:</span>{' '}
                    <span className="text-gray-900">{visitaToDelete.usuario || '—'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Coordenadas:</span>{' '}
                    <span className="text-gray-900 font-mono">{visitaToDelete.coords || '—'}</span>
                  </div>
                  {visitaToDelete.visita && (
                    <div>
                      <span className="font-medium text-gray-700">Motivo:</span>{' '}
                      <span className="text-gray-900">{visitaToDelete.visita}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VisitasManagement;
